import _                         from 'underscore';
import { v4 as uuid }            from 'uuid';
import React, { FC, useContext } from 'react';
import { Form }                  from 'semantic-ui-react';
import { Formik }                from 'formik';
import * as Yup                  from 'yup';

import { runIfFunction }                        from '../../../../util';
import { SystemAccountingQueries, UserQueries } from '../../../../queries';
import { Align, Size, Uuid }                    from '../../../../types';
import { QueryDialogProps, QueryOpDialog }      from '../../../components/from-query/QueryOpDialog';
import { BasicDialogContainer }                 from '../../../components/modals/BasicDialogContainer';
import { TextField }                            from '../../../components/forms/formik';
import { ProjectContext }                       from '../ProjectIndexPage';
import { useCurrentUser }                       from '../../../../hooks';
import { useApolloClient }                      from '@apollo/react-hooks';

interface Props extends QueryDialogProps {
	projectId?: Uuid,
	projectName?: string,
}

interface FormValues {
	projectName: string
}

const defaultProps: Props = {
	projectId:   null,
	projectName: null,
}

const DuplicateProjectDialog: FC<Props> = React.memo<Props>( ( props ) => {

	let {
		    projectId:   currentProjectId = null,
		    projectName: currentProjectName
	    }               = useContext( ProjectContext ),
	    { currentUser } = useCurrentUser(),
	    apolloClient    = useApolloClient();

	const projectId    = currentProjectId ?? props.projectId,
	      projectName  = currentProjectName ?? props.projectName ?? 'New Project',
	      newProjectId = uuid();

	const getVersionedProjectName = () => {

		let existingProjectNames = [ ..._.map( currentUser.projects ?? [], item => item.project.name ) ],
		    sameNames            = _.filter( existingProjectNames, name => ( name.trim() === projectName ) );

		return `${ projectName } v${ sameNames.length + 1 }`;
	}

	return (

		<Formik<FormValues> initialValues={ { projectName: getVersionedProjectName() } }
		                    validateOnChange={ true }
		                    validationSchema={ Yup.object( {
			                    projectName: Yup.string().test( 'unique name', 'Project names must be unique', ( value: string ) => {

				                    let existingProjectNames = [ ..._.map( currentUser.projects ?? [], item => item.project.name ) ];

				                    return !existingProjectNames.includes( value?.trim() ?? '' );

			                    } ).required( 'Project name cannot be blank.' )
		                    } ) }
		                    onSubmit={ null }>

			{ formik => (

				<QueryOpDialog
					{ ...props }
					operationDisabled={ !formik.isValid }
					onComplete={ () => runIfFunction( props.onComplete, { id: newProjectId, name: formik.values.projectName } ) }
					mutation={ SystemAccountingQueries.mutations.DUPLICATE_PROJECT( SystemAccountingQueries.fragments.PROJECTS.EDIT_FIELDS() ) }
					graphArgs={ {
						variables:           {
							input: {
								id:        projectId,
								cloneId:   newProjectId,
								cloneName: formik.values.projectName.trim(),
								fullClone: true,
							}
						},
						awaitRefetchQueries: true,
						refetchQueries:      [
							{ query: UserQueries.queries.GET_CURRENT_USER() }
						],
						update:              async ( store: any, response: any ) => {

							// update current user with newly evaluated project list
							let user        = await apolloClient.query( { query: UserQueries.queries.GET_CURRENT_USER() } ),
							    updatedData = user.data.me;

							store.writeQuery( {
								query: UserQueries.queries.GET_CURRENT_USER(),
								data:  { me: updatedData }
							} );
						}
					} }
					submitLabel={ 'Save' }
					className={ 'no-padding' }
					alignControls={ Align.CENTER }>


					<Form>

						<BasicDialogContainer title={ 'Duplicate Project' } roundedTop contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }>

							<TextField
								autoFocus={ true }
								type={ 'text' }
								descriptionText={ 'Choose a unique name that will help your team identify this project’s purpose.' }
								size={ 'big' }
								icon={ 'briefcase' }
								iconPosition='left'
								name={ 'projectName' }
								label={ `New Project's Name` }
							/>

						</BasicDialogContainer>
					</Form>

				</QueryOpDialog>
			) }

		</Formik>
	)
} );

DuplicateProjectDialog.defaultProps = defaultProps;

export { DuplicateProjectDialog };
